import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import {
  Lot,
  Serial,
  OnHandBatch,
  OnHandSerial,
  BatchSerialFilters,
  LotFormData,
  SerialFormData,
  MovementHistory,
  BinTransferRequest,
  BinTransferResponse,
  TraceabilityResponse,
} from '@/types/inventory-traceability';

export const useTraceability = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Fetch lots with filters and pagination
  const fetchLots = useCallback(async (
    filters: BatchSerialFilters = {},
    page = 0,
    pageSize = 50
  ): Promise<TraceabilityResponse<OnHandBatch>> => {
    setLoading(true);
    try {
      let query = supabase
        .from('on_hand_batch_v')
        .select(`
          *,
          item:bom_components!item_id(component_name, unit, trace_type)
        `)
        .order('exp_date', { ascending: true, nullsLast: true })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      // Apply filters
      if (filters.item_id) {
        query = query.eq('item_id', filters.item_id);
      }
      if (filters.warehouse_id) {
        query = query.eq('warehouse_id', filters.warehouse_id);
      }
      if (filters.status) {
        query = query.eq('lot_status', filters.status);
      }
      if (filters.lot_code) {
        query = query.ilike('lot_code', `%${filters.lot_code}%`);
      }
      if (filters.exp_date_from) {
        query = query.gte('exp_date', filters.exp_date_from);
      }
      if (filters.exp_date_to) {
        query = query.lte('exp_date', filters.exp_date_to);
      }
      if (filters.search) {
        query = query.or(`lot_code.ilike.%${filters.search}%,warehouse_code.ilike.%${filters.search}%,item_code.ilike.%${filters.search}%`);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        count: count || 0,
      };
    } catch (error) {
      console.error('Error fetching lots:', error);
      toast({
        title: "Error",
        description: "Failed to fetch lot information.",
        variant: "destructive",
      });
      return { data: [], count: 0, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch serials with filters and pagination
  const fetchSerials = useCallback(async (
    filters: BatchSerialFilters = {},
    page = 0,
    pageSize = 50
  ): Promise<TraceabilityResponse<OnHandSerial>> => {
    setLoading(true);
    try {
      let query = supabase
        .from('on_hand_serial_v')
        .select(`
          *,
          item:bom_components!item_id(component_name, unit, trace_type)
        `)
        .order('last_movement', { ascending: false, nullsLast: true })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      // Apply filters
      if (filters.item_id) {
        query = query.eq('item_id', filters.item_id);
      }
      if (filters.warehouse_id) {
        query = query.eq('warehouse_id', filters.warehouse_id);
      }
      if (filters.status) {
        query = query.eq('serial_status', filters.status);
      }
      if (filters.serial_no) {
        query = query.ilike('serial_no', `%${filters.serial_no}%`);
      }
      if (filters.search) {
        query = query.or(`serial_no.ilike.%${filters.search}%,warehouse_code.ilike.%${filters.search}%,item_code.ilike.%${filters.search}%`);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        count: count || 0,
      };
    } catch (error) {
      console.error('Error fetching serials:', error);
      toast({
        title: "Error",
        description: "Failed to fetch serial information.",
        variant: "destructive",
      });
      return { data: [], count: 0, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Create new lot
  const createLot = useCallback(async (lotData: LotFormData): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('lot')
        .insert({
          ...lotData,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Lot ${lotData.lot_code} created successfully.`,
      });

      return true;
    } catch (error) {
      console.error('Error creating lot:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create lot.",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Create new serial
  const createSerial = useCallback(async (serialData: SerialFormData): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('serial')
        .insert({
          ...serialData,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Serial ${serialData.serial_no} created successfully.`,
      });

      return true;
    } catch (error) {
      console.error('Error creating serial:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create serial.",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Update lot status
  const updateLotStatus = useCallback(async (
    lotId: string,
    status: 'ACTIVE' | 'HOLD' | 'CLOSED'
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('lot')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', lotId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Lot status updated to ${status}.`,
      });

      return true;
    } catch (error) {
      console.error('Error updating lot status:', error);
      toast({
        title: "Error",
        description: "Failed to update lot status.",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Update serial status
  const updateSerialStatus = useCallback(async (
    serialId: string,
    status: 'ACTIVE' | 'HOLD' | 'SCRAP'
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('serial')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', serialId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Serial status updated to ${status}.`,
      });

      return true;
    } catch (error) {
      console.error('Error updating serial status:', error);
      toast({
        title: "Error",
        description: "Failed to update serial status.",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Get movement history for lot or serial
  const getMovementHistory = useCallback(async (
    type: 'lot' | 'serial',
    id: string
  ): Promise<MovementHistory[]> => {
    setLoading(true);
    try {
      const column = type === 'lot' ? 'lot_id' : 'serial_id';
      
      const { data, error } = await supabase
        .from('inventory_tx')
        .select(`
          id,
          txn_type,
          qty,
          uom,
          wh_from:warehouse!wh_from(code, name),
          wh_to:warehouse!wh_to(code, name),
          bin_from:warehouse_bin!bin_from(code, name),
          bin_to:warehouse_bin!bin_to(code, name),
          ref_table,
          ref_id,
          note,
          created_at,
          created_by_user:users!created_by(full_name)
        `)
        .eq(column, id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error fetching movement history:', error);
      toast({
        title: "Error",
        description: "Failed to fetch movement history.",
        variant: "destructive",
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Bin transfer function
  const performBinTransfer = useCallback(async (
    transferData: BinTransferRequest
  ): Promise<BinTransferResponse> => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('fn_bin_transfer', {
        p_item_id: transferData.item_id,
        p_qty: transferData.qty,
        p_wh_from: transferData.wh_from,
        p_bin_from: transferData.bin_from,
        p_wh_to: transferData.wh_to,
        p_bin_to: transferData.bin_to,
        p_lot_id: transferData.lot_id,
        p_serial_id: transferData.serial_id,
        p_ref_table: transferData.ref_table || 'manual_transfer',
        p_ref_id: transferData.ref_id,
        p_note: transferData.note || 'Bin transfer',
      });

      if (error) throw error;

      const result = data as BinTransferResponse;
      
      if (result.success) {
        toast({
          title: "Success",
          description: result.message,
        });
      } else {
        throw new Error(result.error || 'Transfer failed');
      }

      return result;
    } catch (error) {
      console.error('Error performing bin transfer:', error);
      const errorResult: BinTransferResponse = {
        success: false,
        tx_ids: [],
        message: '',
        error: error.message || 'Failed to perform bin transfer',
      };
      
      toast({
        title: "Error",
        description: errorResult.error,
        variant: "destructive",
      });
      
      return errorResult;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Split lot function
  const splitLot = useCallback(async (
    lotId: string,
    splitQty: number,
    newLotCode: string
  ): Promise<boolean> => {
    setLoading(true);
    try {
      // Get original lot details
      const { data: originalLot, error: fetchError } = await supabase
        .from('lot')
        .select('*')
        .eq('id', lotId)
        .single();

      if (fetchError) throw fetchError;

      if (originalLot.qty_open < splitQty) {
        throw new Error('Split quantity cannot exceed available quantity');
      }

      // Create new lot with split quantity
      const { error: createError } = await supabase
        .from('lot')
        .insert({
          item_id: originalLot.item_id,
          lot_code: newLotCode,
          mfg_date: originalLot.mfg_date,
          exp_date: originalLot.exp_date,
          qty_open: splitQty,
          supplier_id: originalLot.supplier_id,
          notes: `Split from lot ${originalLot.lot_code}`,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        });

      if (createError) throw createError;

      // Update original lot quantity
      const { error: updateError } = await supabase
        .from('lot')
        .update({ 
          qty_open: originalLot.qty_open - splitQty,
          updated_at: new Date().toISOString()
        })
        .eq('id', lotId);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: `Lot split successfully. New lot ${newLotCode} created with quantity ${splitQty}.`,
      });

      return true;
    } catch (error) {
      console.error('Error splitting lot:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to split lot.",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    loading,
    fetchLots,
    fetchSerials,
    createLot,
    createSerial,
    updateLotStatus,
    updateSerialStatus,
    getMovementHistory,
    performBinTransfer,
    splitLot,
  };
};
